import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    LinearProgress,
    Alert,
    Chip,
    Collapse,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import {
    Download,
    FileDownload,
    ExpandMore,
    ExpandLess,
    Settings,
} from "@mui/icons-material";
import {
    startExtractSpendingsUserAPI,
    getExtractStatusAPI,
    downloadExtractAPI,
} from "../../../api/spendingsExtractAPI";

// -----------------------------------------------------------------------------
// Field config (Spending entity) — mirrors desktop-v2 ExtractButton structure
// -----------------------------------------------------------------------------
const SPENDING_EXTRACT_FIELDS_CONFIG = {
    spending_id: { label: "ID", category: "Identité" },
    spending_user: { label: "Utilisateur", category: "Identité" },
    spending_user_name: { label: "Utilisateur (Nom)", category: "Identité" },
    spending_category: { label: "Catégorie", category: "Classification" },
    spending_category_name: { label: "Catégorie (Nom)", category: "Classification" },
    spending_supplier: { label: "Fournisseur", category: "Classification" },
    spending_supplier_name: { label: "Fournisseur (Nom)", category: "Classification" },
    spending_project: { label: "Projet", category: "Classification" },
    spending_project_name: { label: "Projet (Nom)", category: "Classification" },
    spending_chantier: { label: "Chantier", category: "Classification" },
    spending_chantier_name: { label: "Chantier (Nom)", category: "Classification" },
    spending_equipement: { label: "Équipement", category: "Classification" },
    spending_equipement_name: { label: "Équipement (Nom)", category: "Classification" },
    admin_account_id: { label: "Compte Admin", category: "Identité" },
    admin_account_name: { label: "Compte Admin (Nom)", category: "Identité" },
    spending_amount: { label: "Montant HT", category: "Financier" },
    spending_TVA: { label: "TVA", category: "Financier" },
    spending_taxes: { label: "Taxes", category: "Financier" },
    spending_amountTTC: { label: "Montant TTC", category: "Financier" },
    spending_total_payed: { label: "Total Payé", category: "Financier" },
    spending_rest_to_pay: { label: "Reste à Payer", category: "Financier" },
    spending_fully_paid: { label: "Entièrement Payé", category: "Financier" },
    spending_desc: { label: "Description", category: "Descriptif" },
    spending_remarks: { label: "Remarques", category: "Descriptif" },
    spending_labels: { label: "Labels", category: "Descriptif" },
    type_cb: { label: "Type CB", category: "Descriptif" },
    spending_date: { label: "Date", category: "Temporel" },
    spending_update_date: { label: "Date Modification", category: "Temporel" },
    spending_status: { label: "Statut", category: "Statut" },
    spending_demand: { label: "Demande de dépense liée", category: "Statut" },
    spending_demand_name: { label: "Demande de dépense (Nom)", category: "Statut" },
};

const ALL_SPENDING_FIELDS = Object.keys(SPENDING_EXTRACT_FIELDS_CONFIG);
const ESSENTIAL_SPENDING_FIELDS = ["spending_id", "spending_status"];

// -----------------------------------------------------------------------------
// Filter -> structured params
// -----------------------------------------------------------------------------
/**
 * Builds structured filter params for the extract API. The backend validates
 * each field and constructs the N1QL WHERE clauses server-side, preventing
 * injection via raw query strings.
 *
 * Accepted server-side params: month (1-12), year (2000-2100), category,
 * subCategory, supplier, status.
 */
const buildExtractParamsFromFilters = (filters) => {
    const out = {};
    if (!filters) return out;

    const { searchMonth, searchYear, searchCategory, searchSubCategory, searchSupplier, spend_status } = filters;

    // searchMonth may be select object { value, label } or primitive.
    // FilterSpendings uses a 0-based month index — the backend expects 1-12.
    const monthRaw =
        searchMonth && typeof searchMonth === "object" && !Array.isArray(searchMonth)
            ? searchMonth.value
            : searchMonth;
    const yearRaw =
        searchYear && typeof searchYear === "object" && !Array.isArray(searchYear)
            ? searchYear.value
            : searchYear;

    if (yearRaw !== undefined && yearRaw !== null && yearRaw !== "") {
        const year = parseInt(yearRaw, 10);
        if (!Number.isNaN(year)) out.year = year;
    }
    if (monthRaw !== undefined && monthRaw !== null && monthRaw !== "") {
        const month = parseInt(monthRaw, 10);
        if (!Number.isNaN(month)) out.month = month + 1; // 0-based -> 1-based
    }

    const categoryValue =
        searchCategory && typeof searchCategory === "object"
            ? searchCategory.value
            : searchCategory;
    if (categoryValue) out.category = categoryValue;
    if (searchSubCategory) out.subCategory = searchSubCategory;
    if (searchSupplier) out.supplier = searchSupplier;
    if (spend_status) out.status = spend_status;

    return out;
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
export default function SpendingExtractButton({ filters }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const [selectedFields, setSelectedFields] = useState([...ALL_SPENDING_FIELDS]);
    const [processID, setProcessID] = useState(null);
    const [extractStatus, setExtractStatus] = useState({ status: "", total: 0, actual: 0 });
    const [isStarting, setIsStarting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const abortedRef = useRef(false);
    const pollRef = useRef(null);

    // Unmount cleanup: abort any in-flight setState and stop polling
    useEffect(() => {
        abortedRef.current = false;
        return () => {
            abortedRef.current = true;
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, []);

    // Poll status when processID is set and not yet terminal
    useEffect(() => {
        if (!processID) return undefined;
        if (extractStatus.status === "COMPLETED" || extractStatus.status === "ERROR") {
            return undefined;
        }

        const interval = setInterval(async () => {
            try {
                const res = await getExtractStatusAPI(processID);
                if (abortedRef.current) return;
                const data = (res && res.data) || {};
                setExtractStatus({
                    status: data.status || "",
                    total: data.total || 0,
                    actual: data.actual || 0,
                });
                if (data.status === "COMPLETED" || data.status === "ERROR") {
                    clearInterval(interval);
                    pollRef.current = null;
                }
            } catch (err) {
                if (abortedRef.current) return;
                console.log("[ERROR] getExtractStatus ", err);
                setErrorMsg("Erreur lors de la récupération du statut");
                setExtractStatus((prev) => ({ ...prev, status: "ERROR" }));
                clearInterval(interval);
                pollRef.current = null;
            }
        }, 2000);

        pollRef.current = interval;
        return () => {
            clearInterval(interval);
            if (pollRef.current === interval) {
                pollRef.current = null;
            }
        };
    }, [processID, extractStatus.status]);

    const toggleExpanded = () => setIsExpanded((prev) => !prev);
    const toggleFieldSelector = () => setShowFieldSelector((prev) => !prev);

    const handleFieldChange = (field, checked) => {
        if (!checked && ESSENTIAL_SPENDING_FIELDS.includes(field)) return;
        setSelectedFields((prev) => {
            if (checked) {
                if (prev.includes(field)) return prev;
                return [...prev, field];
            }
            return prev.filter((f) => f !== field);
        });
    };

    const selectAllFields = () => setSelectedFields([...ALL_SPENDING_FIELDS]);
    const resetToDefaultFields = () => setSelectedFields([...ALL_SPENDING_FIELDS]);

    const handleStartExtract = async () => {
        setErrorMsg("");
        setIsStarting(true);
        setExtractStatus({ status: "", total: 0, actual: 0 });
        setProcessID(null);

        try {
            const params = {
                orderBy: "spending_date DESC",
                ...buildExtractParamsFromFilters(filters),
            };
            if (selectedFields.length > 0 && selectedFields.length < ALL_SPENDING_FIELDS.length) {
                params.fields = selectedFields;
            }

            const res = await startExtractSpendingsUserAPI(params);
            if (abortedRef.current) return;
            const data = (res && res.data) || {};
            const pid = data.processID || data.process_id;
            if (!pid) {
                setErrorMsg("Aucun processID renvoyé par le serveur");
                return;
            }
            setProcessID(pid);
        } catch (err) {
            if (abortedRef.current) return;
            console.log("[ERROR] startExtract ", err);
            setErrorMsg("Erreur au démarrage de l'extraction");
        } finally {
            if (!abortedRef.current) setIsStarting(false);
        }
    };

    const handleDownload = async () => {
        if (!processID) return;
        setIsDownloading(true);
        setErrorMsg("");
        try {
            const res = await downloadExtractAPI(processID);
            if (abortedRef.current) return;
            const blobData = res && res.data ? res.data : res;
            const blob = new Blob([blobData], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const today = new Date();
            const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
            link.href = url;
            link.download = `spendings_extract_${dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (abortedRef.current) return;
            console.log("[ERROR] downloadExtract ", err);
            setErrorMsg("Erreur au téléchargement du fichier");
        } finally {
            if (!abortedRef.current) setIsDownloading(false);
        }
    };

    const isExtracting = isStarting || (!!processID && extractStatus.status !== "COMPLETED" && extractStatus.status !== "ERROR");

    const getStatusColor = (status) => {
        switch (status) {
            case "PENDING":
                return "warning";
            case "COMPLETED":
                return "success";
            case "ERROR":
                return "error";
            default:
                return "default";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "PENDING":
                return "En cours...";
            case "COMPLETED":
                return "Terminé";
            case "ERROR":
                return "Erreur";
            case "NOT_FOUND":
                return "Introuvable";
            default:
                return "Prêt";
        }
    };

    const getProgressPercentage = () => {
        if (!extractStatus.total) return 0;
        return Math.round((extractStatus.actual / extractStatus.total) * 100);
    };

    // Group fields by category for display
    const fieldsByCategory = ALL_SPENDING_FIELDS.reduce((acc, field) => {
        const category = SPENDING_EXTRACT_FIELDS_CONFIG[field].category;
        if (!acc[category]) acc[category] = [];
        acc[category].push(field);
        return acc;
    }, {});

    return (
        <Box
            sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 0.5,
                backgroundColor: "#fafafa",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Bouton compact */}
            <Box
                sx={{
                    p: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    minHeight: "32px",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                }}
                onClick={toggleExpanded}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FileDownload sx={{ fontSize: 14 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>
                        CSV
                    </Typography>
                    {(extractStatus.status || isExtracting) && (
                        <Chip
                            label={
                                isExtracting && !extractStatus.status
                                    ? "..."
                                    : extractStatus.status === "PENDING"
                                        ? "..."
                                        : extractStatus.status === "COMPLETED"
                                            ? "OK"
                                            : extractStatus.status === "ERROR"
                                                ? "!"
                                                : "..."
                            }
                            color={
                                isExtracting && !extractStatus.status
                                    ? "warning"
                                    : getStatusColor(extractStatus.status || "PENDING")
                            }
                            size="small"
                            sx={{ height: 16, fontSize: "0.6rem", minWidth: 16 }}
                        />
                    )}
                </Box>
                {isExpanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
            </Box>

            {/* Contenu expandable */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 0.5, borderTop: "1px solid #e0e0e0" }}>
                    {/* Boutons d'action */}
                    {!isExtracting && extractStatus.status !== "COMPLETED" && (
                        <Box sx={{ mb: 0.5, display: "flex", gap: 0.5 }}>
                            <button
                                onClick={handleStartExtract}
                                disabled={isStarting || isExtracting}
                                style={{
                                    width: "120px",
                                    height: "18px",
                                    fontSize: "10px",
                                    backgroundColor: "#ffc107",
                                    color: "#000",
                                    border: "none",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    padding: "0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: (isStarting || isExtracting) ? 0.7 : 1,
                                }}
                            >
                                {(isStarting || isExtracting) ? "Démarrage..." : "Extraire"}
                            </button>
                            <button
                                onClick={toggleFieldSelector}
                                style={{
                                    width: "140px",
                                    height: "18px",
                                    fontSize: "10px",
                                    backgroundColor: "#6c757d",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    padding: "0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "4px",
                                }}
                            >
                                <Settings sx={{ fontSize: 8 }} />
                                {showFieldSelector ? "Masquer champs" : "Configurer champs"}
                            </button>
                        </Box>
                    )}

                    {/* Sélecteur de champs */}
                    <Collapse in={showFieldSelector}>
                        <Box
                            sx={{
                                mb: 0.5,
                                p: 0.5,
                                backgroundColor: "#f8f9fa",
                                borderRadius: "3px",
                                border: "1px solid #e0e0e0",
                                maxHeight: "240px",
                                overflowY: "auto",
                                width: "100%",
                            }}
                        >
                            <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                                Sélectionner les champs ({selectedFields.length}/{ALL_SPENDING_FIELDS.length})
                            </Typography>

                            {/* Boutons d'actions rapides */}
                            <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                                <button
                                    onClick={resetToDefaultFields}
                                    style={{
                                        height: "18px",
                                        fontSize: "9px",
                                        backgroundColor: "#17a2b8",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "2px",
                                        cursor: "pointer",
                                        padding: "0 6px",
                                    }}
                                >
                                    Défaut
                                </button>
                                <button
                                    onClick={selectAllFields}
                                    style={{
                                        height: "18px",
                                        fontSize: "9px",
                                        backgroundColor: "#28a745",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "2px",
                                        cursor: "pointer",
                                        padding: "0 6px",
                                    }}
                                >
                                    Tout
                                </button>
                            </Box>

                            {/* Champs par catégorie */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                                {Object.entries(fieldsByCategory).map(([category, fields]) => (
                                    <Box key={category} sx={{ flex: "0 1 auto", minWidth: "180px", maxWidth: "250px", alignSelf: "flex-start" }}>
                                        <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                                            {category} ({fields.filter((f) => selectedFields.includes(f)).length}/{fields.length})
                                        </Typography>
                                        <FormGroup>
                                            {fields.map((field) => (
                                                <FormControlLabel
                                                    key={field}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedFields.includes(field)}
                                                            onChange={(e) => handleFieldChange(field, e.target.checked)}
                                                            disabled={ESSENTIAL_SPENDING_FIELDS.includes(field)}
                                                            sx={{
                                                                "& .MuiSvgIcon-root": { fontSize: 14 },
                                                                py: 0,
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                                            {SPENDING_EXTRACT_FIELDS_CONFIG[field].label}
                                                            {ESSENTIAL_SPENDING_FIELDS.includes(field) && " *"}
                                                        </Typography>
                                                    }
                                                    sx={{ mb: 0, py: 0 }}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                ))}
                            </Box>

                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mt: 0.5 }}>
                                * Champs obligatoires
                            </Typography>
                        </Box>
                    </Collapse>

                    {/* Affichage détaillé du statut */}
                    {(extractStatus.status || isExtracting) && (
                        <Box sx={{ mb: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                    {extractStatus.status === "PENDING" || (isExtracting && !extractStatus.status)
                                        ? `${extractStatus.actual || 0} / ${extractStatus.total || 0}`
                                        : getStatusText(extractStatus.status || "PENDING")}
                                </Typography>
                            </Box>

                            {(extractStatus.status === "PENDING" || (isExtracting && !extractStatus.status)) && (
                                <LinearProgress
                                    variant={extractStatus.total > 0 ? "determinate" : "indeterminate"}
                                    value={extractStatus.total > 0 ? getProgressPercentage() : undefined}
                                    sx={{ mb: 0.5, height: 4 }}
                                />
                            )}

                            {extractStatus.status === "ERROR" && (
                                <Alert severity="error" sx={{ mb: 0.5, py: 0.25, fontSize: "0.7rem" }}>
                                    Erreur d'extraction
                                </Alert>
                            )}

                            {isExtracting && !extractStatus.status && (
                                <Alert severity="info" sx={{ mb: 0.5, py: 0.25, fontSize: "0.7rem" }}>
                                    Initialisation...
                                </Alert>
                            )}
                        </Box>
                    )}

                    {errorMsg && (
                        <Alert severity="error" sx={{ mb: 0.5, py: 0.25, fontSize: "0.7rem" }}>
                            {errorMsg}
                        </Alert>
                    )}

                    {/* Bouton de téléchargement */}
                    {extractStatus.status === "COMPLETED" && processID && (
                        <button
                            onClick={handleDownload}
                            disabled={isDownloading}
                            style={{
                                width: "100%",
                                height: "18px",
                                fontSize: "10px",
                                backgroundColor: "#4caf50",
                                color: "#fff",
                                border: "none",
                                borderRadius: "3px",
                                cursor: "pointer",
                                fontWeight: "500",
                                padding: "0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                                opacity: isDownloading ? 0.7 : 1,
                            }}
                        >
                            <Download sx={{ fontSize: 8 }} />
                            {isDownloading ? "Téléchargement..." : "Télécharger"}
                        </button>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontSize: "0.65rem" }}>
                        Prend en compte les filtres appliqués
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
}
